import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AvatarWithUsername, CellBorderWidthEnum, DateTimePicker, DropdownMenu, ModularTable, NotificationDispatch, SortEnum, StatusTagEnum, TableColorThemeEnum, Tag, Tooltip, useViewport } from '@tryhackme/thm-ui-components';
import { addMonths, format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { AvatarWithRoom } from 'src/common/components';
import { RoomStatus, RoomSubmissionAction } from 'src/common/enums/rooms';
import type { RoomType } from 'src/common/interfaces/room';
import { formatDateAgo } from 'src/features/user-feedback/user-feedback.helpers';
import { ScheduleRoomErrorMessage, validSortValues } from '../../submissions.constants';
import { sortData } from '../../submissions.helpers';
import { useUpdateSubmissionMutation } from '../../submissions.slice';
import { StyledButton, StyledDateTimePicker, StyledDivider, StyledDropdownMenuItem, StyledTooltipText, StyledTriggerElement } from '../../submissions.styles';
import type { Submission, SubmissionRowType, SubmissionsTableProps, SubmissionsTableType } from '../../submissions.types';
import { AddCommentModal, CommentsModal, StatusModal } from '../modals';
import { TypeTag } from '../type-tag/type-tag';
const roomStatusToTagColor: { [key in RoomStatus]: StatusTagEnum } = {
  [RoomStatus.READY]: StatusTagEnum.GREEN,
  [RoomStatus.REJECTED]: StatusTagEnum.RED,
  [RoomStatus.SUBMITTED]: StatusTagEnum.BLUE,
  [RoomStatus.EVALUATING]: StatusTagEnum.ORANGE,
  [RoomStatus.ACCEPTED]: StatusTagEnum.PURPLE,
  [RoomStatus.UNRELEASED]: StatusTagEnum.GREY
};
const formatDateToGMT = (item: React.ReactNode) => {
  if (!item) {
    return null;
  }
  const originalDate = parseISO(item.toString());
  const zonedDate = utcToZonedTime(originalDate, 'GMT');
  const formattedDate = format(zonedDate, 'MMM do yyyy HH:mm');
  return formattedDate;
};
const StatefullDateTimePicker: FC<{
  item: string | null | undefined;
  row: SubmissionRowType;
  handleScheduleChange: (status: string, expiryDate: number, roomCode: string) => Promise<void>;
}> = ({
  item,
  row,
  handleScheduleChange
}) => {
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>();
  useEffect(() => {
    setSelectedDateTime(item ? new Date(item) : null);
  }, [item]);
  return <DateTimePicker label="" selected={selectedDateTime} placeholderText="Select date" minDate={new Date()} maxDate={addMonths(new Date(), 12)} showTimeSelect timeIntervals={15} shouldCloseOnSelect onChange={(expiryDate: Date) => {
    setSelectedDateTime(expiryDate);
  }}
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  onCalendarClose={async () => {
    await handleScheduleChange(row.status, selectedDateTime ? selectedDateTime.getTime() : 0, row.roomCode);
  }} dateFormat="MMM do yyyy HH:mm" data-sentry-element="DateTimePicker" data-sentry-component="StatefullDateTimePicker" data-sentry-source-file="submissions-table.tsx" />;
};
export const SubmissionsTable: FC<SubmissionsTableProps> = ({
  submissions,
  isLoading,
  activeSortColumn,
  setActiveSortColumn,
  submissionsParams,
  setSubmissionsParams
}) => {
  const [updateSubmission] = useUpdateSubmissionMutation();
  const [openDropdownId, setOpenDropdownId] = useState<string>('');
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState<string>('');
  const getCommentsByRoomCode = (roomCode: string) => {
    const submission = submissions?.data?.docs?.find((doc: Submission) => doc.data.code === roomCode);
    return submission?.data?.checked.comments;
  };
  const theme = useTheme();
  const {
    width
  } = useViewport();

  // fix to have scrollable table in the tablet view since current table implementation doesn't support it
  const isTablet = useMemo(() => width > Number.parseInt(theme.sizes.md, 10) && width < Number.parseInt(theme.sizes.lg, 10), [width, theme]);
  const handleScheduleChange = async (status: string, expiryDate: number, roomCode: string) => {
    if (status === RoomStatus.READY) {
      await updateSubmission({
        roomCode,
        action: expiryDate === 0 ? RoomSubmissionAction.CLEAR_DATE : RoomSubmissionAction.SCHEDULE,
        date: expiryDate,
        comment: ''
      });
    } else NotificationDispatch('error', ScheduleRoomErrorMessage);
  };

  // Format data for table
  const formatSubmissionToRow = (submission: Submission): SubmissionsTableType['rows'][number] => ({
    id: submission._id,
    makerUsername: submission.maker?.username,
    makerAvatar: submission.maker?.avatar,
    userWriteupUrl: submission.data?.userWriteup,
    testerUsername: submission.tester?.username,
    testerAvatar: submission.tester?.avatar,
    roomTitle: submission.data.title,
    roomAvatar: submission.data.imageURL,
    roomType: submission.data.type,
    status: submission.data.checked.status,
    requested: submission.data.checked.requested ? formatDateAgo(submission.data.checked.requested) : null,
    scheduled: submission.data.checked.scheduled,
    commissioned: submission.data.checked.commissioned,
    roomCode: submission.data.code
  });
  const formattedRows: SubmissionsTableType['rows'] = submissions?.data?.docs ? submissions.data.docs.map((doc: Submission) => formatSubmissionToRow(doc)) : [];
  const submissionsTableColumns: SubmissionsTableType['columns'] = [{
    key: 'roomTitle',
    title: 'Room',
    sortFunction: () => null,
    width: '0',
    padding: '1.1rem 1.6rem',
    flex: isTablet ? '0 0 20%' : '0 0 15%',
    render: (item, row) => <AvatarWithRoom roomCode={row.roomCode} roomTitle={item as string} avatar={row.roomAvatar} />
  }, {
    key: 'makerUsername',
    title: 'Maker',
    sortFunction: () => null,
    width: '0',
    padding: '1.1rem 1.6rem',
    flex: isTablet ? '0 0 20%' : '0 0 15%',
    render: (item, row) => <AvatarWithUsername username={item as string} avatar={row.makerAvatar as string} />
  }, {
    key: 'roomType',
    title: 'Type',
    sortFunction: () => null,
    width: '0',
    flex: isTablet ? '0 0 17%' : '0 0 12%',
    render: (item, row) => <TypeTag type={item as RoomType} userWriteupUrl={row.userWriteupUrl} />
  }, {
    key: 'testerUsername',
    title: 'Tester',
    sortFunction: () => null,
    width: '0',
    flex: isTablet ? '0 0 20%' : '0 0 15%',
    padding: '1.1rem 1.6rem',
    render: (item, row) => <AvatarWithUsername username={item as string} avatar={row.testerAvatar as string} />
  }, {
    key: 'requested',
    title: 'Requested',
    sortFunction: () => null,
    width: '0',
    flex: isTablet ? '0 0 14%' : '0 0 10%'
  }, {
    key: 'status',
    title: 'Status',
    sortFunction: () => null,
    justifyContent: 'center',
    width: '0',
    flex: isTablet ? '0 0 15%' : '0 0 11%',
    render: item => {
      const status = item as RoomStatus;
      const statusTagType = roomStatusToTagColor[status];
      const tagText = status.charAt(0).toUpperCase() + status.slice(1);
      return <Tag size="default" status={statusTagType || StatusTagEnum.GREY}>
            {tagText}
          </Tag>;
    }
  }, {
    key: 'scheduled',
    title: 'Release Schedule',
    sortFunction: () => null,
    width: '0',
    flex: isTablet ? '0 0 22%' : '0 0 17%',
    render: (item, row) => {
      const formattedDate = formatDateToGMT(item);
      return <StyledDateTimePicker>
            {formattedDate && <Tooltip arrowPosition="bottom-center">
                <StyledTooltipText>{`GMT: ${formattedDate}`}</StyledTooltipText>
              </Tooltip>}
            <StatefullDateTimePicker item={item} row={row} handleScheduleChange={handleScheduleChange} />
          </StyledDateTimePicker>;
    }
  }, {
    key: 'roomCode',
    title: 'Actions',
    justifyContent: 'center',
    width: '0',
    flex: isTablet ? '0 0 8%' : '0 0 5%',
    render: (item, row) => <DropdownMenu isAnimated open={openDropdownId === item} onOpenChange={isOpen => setOpenDropdownId(isOpen ? item as string : '')} triggerBtn={<StyledButton variant="text" color="secondary" data-testid="trigger-button">
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </StyledButton>}>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <CommentsModal comments={getCommentsByRoomCode(item as string)} triggerElement={<StyledTriggerElement>View Comments</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} />
          </StyledDropdownMenuItem>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StyledTriggerElement onClick={() => setIsAddCommentModalOpen(item as string)}>
              Add Comment
            </StyledTriggerElement>
          </StyledDropdownMenuItem>
          <StyledDivider />
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StatusModal action={RoomSubmissionAction.EVALUATE} triggerElement={<StyledTriggerElement>Evaluate</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} disableAction={row.status === 'ready' && !!row.scheduled} />
          </StyledDropdownMenuItem>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StatusModal action={RoomSubmissionAction.ACCEPT} triggerElement={<StyledTriggerElement>Accept (Notify All)</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} />
          </StyledDropdownMenuItem>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StatusModal action={RoomSubmissionAction.ACCEPT_NO_NOTIFICATION} triggerElement={<StyledTriggerElement>Quiet Accept</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} />
          </StyledDropdownMenuItem>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StatusModal action={RoomSubmissionAction.CLEAR_DATE} triggerElement={<StyledTriggerElement>Clear Date</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} />
          </StyledDropdownMenuItem>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StatusModal action={RoomSubmissionAction.READY} triggerElement={<StyledTriggerElement>Ready</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} />
          </StyledDropdownMenuItem>
          <StyledDropdownMenuItem onSelect={e => e.preventDefault()}>
            <StatusModal action={RoomSubmissionAction.REJECT} triggerElement={<StyledTriggerElement>Reject</StyledTriggerElement>} roomCode={item as string} onClose={() => setOpenDropdownId('')} disableAction={row.status === 'ready' && !!row.scheduled} />
          </StyledDropdownMenuItem>
        </DropdownMenu>
  }];
  const formattedSortableColumn = submissionsTableColumns.map(col => {
    const obj = {
      ...col
    };
    if (obj.sortFunction) {
      obj.sortFunction = () => sortData(setActiveSortColumn, setSubmissionsParams, obj.key);
    }
    return obj;
  });
  return <>
      <ModularTable isLoading={isLoading} activeSortColumn={activeSortColumn} activeSortOrder={validSortValues.includes(submissionsParams.sort) ? SortEnum.descending : SortEnum.ascending} columns={formattedSortableColumn} rows={formattedRows} colorTheme={TableColorThemeEnum.grayWhite} cellBorderWidth={CellBorderWidthEnum.thick} rowHeight="6.4rem" data-sentry-element="ModularTable" data-sentry-source-file="submissions-table.tsx" />
      <AddCommentModal isOpen={!!isAddCommentModalOpen} roomCode={isAddCommentModalOpen} onClose={() => setIsAddCommentModalOpen('')} data-sentry-element="AddCommentModal" data-sentry-source-file="submissions-table.tsx" />
    </>;
};